/**
 * Warm Miniature (ADR-0017): a cutaway of a toy building — cream ground, brown
 * ink outlines, deep warm colours that a cream label can sit on.
 *
 * Every colour that carries a rule was measured, not picked: each floor hue
 * holds a cream digit at 4.7:1 or better, the closest two floor hues are further
 * apart (ΔE 18.7) than the dark palette's were (15.1), and every meter colour
 * stands at 3:1 or better against every chip it can be drawn on. Change one and
 * re-measure — a prettier palette that blurs two floors breaks the game.
 */
export const COLORS = {
  /** The page, and the ground every floor wash sits on. */
  background: 0xf4ecdc,
  /** Brown ink: outlines, the cable, the door seam. */
  ink: 0x3d2c1e,
  /** The lift well. Darker than any floor so the column reads as a shaft. */
  shaft: 0xe6d6b8,
  shaftEdge: 0xc9b089,
  floorSlab: 0xd9c6a2,
  cabin: 0xfffaf0,
  /** The door panels themselves. Neutral — status is carried by the outline, not the fill. */
  cabinDoor: 0xe8d8ba,
  cabinEdge: 0x3d2c1e,
  cabinFull: 0xa8261b,
  /** Label colour on every chip and badge — all floor hues are dark enough to hold it. */
  chipText: 0xfffaf0,
  /** One colour per trait channel. Kept few on purpose — see the UI-overload risk. */
  vip: 0x875a06,
  group: 0x6a4ea6,
  /** Drawn on the chip, so light: it has to stand out against all eight floor hues. */
  groupStripe: 0xe3d8ff,
  courier: 0xa9d6ff,
  patienceOk: 0x9fe3b8,
  patienceLow: 0xffd27a,
  patienceOut: 0xffc4b0,
  meterTrack: 0x2a1d12,
  text: 0x3d2c1e,
  textDim: 0x76593f,
  target: 0xa2461f,
} as const;

/**
 * Everything on a person that is not their ticket (ADR-0018).
 *
 * The ticket is the one loud colour on a person, so none of these may read as a
 * destination: every colour here is further, in ΔE, from every floor hue than
 * the two closest floor hues are from each other (18.7), and clothes are muted
 * outright. `packages/game/test/people.spec.ts` measures both. The first draft
 * failed it twice — a slate-blue courier visor sat 8.6 from floor 7, and a
 * golden blonde 18.3 from floor 2 — which is why this is a test, not a note.
 */
export const PEOPLE = {
  skin: [0xf2cfae, 0xe0ad86, 0xb97f58, 0x8a5a3c],
  hair: [0x2a1d12, 0x5a3a22, 0xcdb48a, 0x8a8a8a],
  /** Clothes: low chroma, always. A red coat would read as floor 5. */
  coats: [0x8c7a68, 0xb3a48f, 0x6e5a4a, 0x6b6458, 0x9a8f84, 0x7d7064],
  suit: 0x2f2a30,
  legs: 0x4a3d32,
  shoes: 0x2a1d12,
  hat: 0x2a1d12,
  helmet: 0xefe9dc,
  visor: 0x2e2b2b,
  /** Accessories: recognisable, never a floor's colour. */
  hatBand: 0xc49a52,
  parcel: 0xd9b27c,
  scarf: 0xc9b8f2,
  sweat: 0x7fc2e8,
  /** Emergency crews (ADR-0029): uniforms, as muted as every other coat. */
  medic: 0x8fa894,
  police: 0x222836,
  policeCap: 0x1b202b,
  badge: 0xd9c27a,
  /** Who they came for: a hood for the arrest, a blanket on the stretcher. */
  hood: 0x8e8a86,
  blanket: 0xb7a9b8,
  cuffs: 0xb8b0a4,
  /**
   * Each building's own people (ADR-0031): a uniform each, as muted as every
   * other coat, and one thing they carry that nobody else does.
   */
  postal: 0x9aa2ab,
  postalCap: 0x2b2f36,
  mailBag: 0x7a5a3e,
  letters: 0xf2ead8,
  housekeeping: 0xa59cab,
  apron: 0xf3eee6,
  towels: 0xe6e2f0,
  commuter: 0x7c8478,
  backpack: 0x5b5f54,
  headphones: 0x2f2f33,
  overalls: 0x3b3536,
  hardHat: 0xc9a24c,
  bucket: 0x9aa3a8,
  /**
   * More of each building's own people (ADR-0043): room service's white
   * jacket and silver cloche, the bellhop's jacket and pillbox cap, the
   * cyclist's helmet and wheel, the chief executive's briefcase, the investor's portfolio.
   */
  waiter: 0xe9e4da,
  cloche: 0xc3c0b8,
  bellhop: 0x7a6266,
  bellhopCap: 0x40353a,
  suitcase: 0x8a7a66,
  cyclist: 0x9a8f7e,
  bikeHelmet: 0xc9cbc6,
  rim: 0xb8b0a4,
  briefcase: 0x4a3b2e,
  investor: 0xb7aea2,
  portfolio: 0x8a7a66,
  /**
   * The hotel's departures (ADR-0051): a traveller's coat and wheeled case for
   * the airport shuttle, a bathrobe and the morning paper for breakfast, and
   * ivory with a bouquet for the couple on their way to be married.
   */
  traveller: 0x8d8478,
  rollingCase: 0x4b4744,
  robe: 0xd5d1c9,
  wedding: 0xeae3d6,
  bouquet: 0xc7a7a9,
  sage: 0x8a9a7a,
} as const;

/**
 * The lights an emergency brings with it (ADR-0029): red and white for the
 * ambulance, red and blue for the police. Light on the building's outside and
 * a beacon on the incident's floor, never a ticket's colour — measured anyway.
 */
export const SIRENS = {
  red: 0xff6b5e,
  blue: 0x6aa8ff,
  white: 0xfff3d6,
} as const;

/** The clothes, as opposed to skin, hair and accessories: these must be muted. */
export const CLOTHES: readonly number[] = [
  ...PEOPLE.coats,
  PEOPLE.suit,
  PEOPLE.legs,
  PEOPLE.shoes,
  PEOPLE.hat,
  PEOPLE.helmet,
  PEOPLE.visor,
  PEOPLE.medic,
  PEOPLE.police,
  PEOPLE.policeCap,
  PEOPLE.hood,
  PEOPLE.blanket,
  PEOPLE.postal,
  PEOPLE.postalCap,
  PEOPLE.housekeeping,
  PEOPLE.apron,
  PEOPLE.commuter,
  PEOPLE.backpack,
  PEOPLE.overalls,
  PEOPLE.waiter,
  PEOPLE.bellhop,
  PEOPLE.bellhopCap,
  PEOPLE.cyclist,
  PEOPLE.bikeHelmet,
  PEOPLE.investor,
  PEOPLE.traveller,
  PEOPLE.robe,
  PEOPLE.wedding,
];

/**
 * The lift's own parts, as drawn in play (ADR-0021): the doors, the motor on the
 * roof, the big cabin's trim. Same rule as people — none may read as a floor —
 * and measured by the same test. Two garage colours fail it (dark brass 16.9 and
 * wood 18.3 from floor 2), so they stay in the garage and never reach the shaft.
 */
export const LIFT_PARTS = {
  brass: 0xc49a52,
  iron: 0x3f3a36,
  steel: 0xb8b0a4,
  steelDark: 0x8a8279,
  glow: 0xffd27a,
  glass: 0xd9e4e6,
  /** Skyline Tower's lift light: the cool line on its glass doors (ADR-0027). */
  light: 0x7fd0e6,
  /** The hotel's rugs: one per guest a landing may hold. */
  rug: 0xc98a8e,
} as const;

/**
 * The departure board on the Transit Centre's platform (ADR-0031): a dark face
 * and a warm display, counting down to the next train. Measured like the rest.
 */
export const DEPARTURES = {
  face: 0x2b2b2e,
  text: 0xf6e7c1,
  rim: 0x5a5650,
} as const;

/**
 * Passengers are coloured by destination floor, so "who is going where" reads
 * as a pattern rather than as a row of numbers to parse one at a time.
 */
const FLOOR_HUES = [
  0x2d6a9c, 0x3c7a2c, 0x9a660b, 0xa8406c, 0x6a4ea6, 0xb13f22, 0x147373, 0x4d5a72,
] as const;

export function floorColor(floor: number): number {
  return FLOOR_HUES[floor % FLOOR_HUES.length] as number;
}

export function floorColorCss(floor: number): string {
  return `#${floorColor(floor).toString(16).padStart(6, '0')}`;
}

/** Display face for numbers and headings. Self-hosted in `public/fonts`, so it works offline. */
export const DISPLAY_FONT = '"Baloo 2", system-ui, -apple-system, "Segoe UI", sans-serif';

export const LAYOUT = {
  /**
   * Bands kept clear at the top and bottom for the overlaid controls.
   *
   * Icon buttons shrink the problem but cannot solve it: the shaft runs the full
   * height, so anything in a corner sits on top of a floor. Reserving the strip
   * is what actually stops the overlap — and at 44px it still costs less than
   * half of the two bars this replaced.
   *
   * The bottom strip held the close-doors button, which only select mode shows,
   * and no player has had select mode since v0.5 (ADR-0003). It stood empty
   * until v0.8 gave it back to the floors: on an iPhone in Safari a 12-floor
   * level gets 51px floors instead of 48, the room a person needs to stand on
   * them. What is left is a margin, so the ground floor does not touch the edge.
   */
  insetTop: 44,
  insetBottom: 6,
  /** Left gutter holding the floor number. */
  gutter: 34,
  /** Width of the shaft column the cabin travels in. */
  shaftWidth: 104,
  /**
   * Each of two shafts side by side (ADR-0034). Two full shafts would leave a
   * 375px phone room for three people on a floor; at this width there is room
   * for four, and a cabin still holds its stop tickets at a readable size.
   */
  twinShaftWidth: 84,
  /** Gap between the shaft and the waiting area. */
  aisle: 12,
  floorPaddingY: 6,
  passengerSize: 30,
  passengerGap: 6,
} as const;
